/**
 * AICams Analytics Tracker
 * Модуль трекинга событий для SPA
 */

import type { BootServices } from '../bootstrap';
import type { EventType, TrackOptions } from './types';
import { apiFetch, getAccessTokenOrNull } from '../utils/api';
import { getClientSessionId } from '../utils/clientSession';

let services: BootServices | null = null;
let pageStartTime = Date.now();
let _webappOpenSentForSession: string | null = null;

type QueuedEvent = { type: EventType; opts: TrackOptions; queuedAt: number };
const _queue: QueuedEvent[] = [];
let _flushInFlight: Promise<void> | null = null;

type ExperimentsContext = {
  experiments?: Record<string, string>;
  exp_config_version?: string;
};

let _experimentsContextCache: { value: ExperimentsContext; expiresAt: number } | null = null;
let _experimentsRefreshOnce: Promise<void> | null = null;

async function ensureExperimentsContextReady(timeoutMs = 1200): Promise<void> {
  try {
    if (!services?.experiments?.refresh) return;
    if (!_experimentsRefreshOnce) {
      _experimentsRefreshOnce = (async () => {
        try {
          await services.experiments.refresh();
        } catch {
          // best-effort
        }
      })();
    }

    // Do not block analytics too long; experiments config is optional.
    await Promise.race([
      _experimentsRefreshOnce,
      new Promise<void>((resolve) => setTimeout(resolve, Math.max(0, timeoutMs))),
    ]);
  } finally {
    // Force a fresh read after refresh (or timeout).
    _experimentsContextCache = null;
  }
}

function readExperimentsContextUncached(): ExperimentsContext {
  try {
    // Fast path: runtime can publish context in-memory.
    const globalCtx = (window as any).__AICAMS_EXPERIMENTS_CONTEXT__;
    if (globalCtx && typeof globalCtx === 'object') {
      const experiments = (globalCtx.experiments && typeof globalCtx.experiments === 'object') ? globalCtx.experiments : undefined;
      const expConfigVersion = (globalCtx.exp_config_version && typeof globalCtx.exp_config_version === 'string')
        ? globalCtx.exp_config_version
        : undefined;
      return { experiments, exp_config_version: expConfigVersion };
    }
  } catch {
    // ignore
  }

  // Fallback: read from localStorage (used by ExperimentsRuntime).
  try {
    const raw = localStorage.getItem('aicams_exp_context_v1');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        const experiments = (parsed.experiments && typeof parsed.experiments === 'object') ? parsed.experiments : undefined;
        const expConfigVersion = (parsed.exp_config_version && typeof parsed.exp_config_version === 'string')
          ? parsed.exp_config_version
          : undefined;
        return { experiments, exp_config_version: expConfigVersion };
      }
    }
  } catch {
    // ignore
  }

  // Legacy/compat fallback: assignments map => { [expKey]: { variant: string } }.
  // We only need the compact { [expKey]: variant } representation for analytics.
  try {
    const raw = localStorage.getItem('aicams_exp_assignments_v1');
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};

    const experiments: Record<string, string> = {};
    for (const [expKey, value] of Object.entries(parsed)) {
      if (!expKey) continue;
      if (value && typeof value === 'object') {
        const variant = (value as any).variant;
        if (variant && typeof variant === 'string') experiments[expKey] = variant;
      } else if (typeof value === 'string') {
        // Extremely compact format: { [expKey]: "A" }
        experiments[expKey] = value;
      }
    }
    return Object.keys(experiments).length ? { experiments } : {};
  } catch {
    return {};
  }
}

function getExperimentsContext(): ExperimentsContext {
  const now = Date.now();
  if (_experimentsContextCache && _experimentsContextCache.expiresAt > now) return _experimentsContextCache.value;
  const value = readExperimentsContextUncached();
  _experimentsContextCache = { value, expiresAt: now + 5_000 };
  return value;
}

function enqueue(type: EventType, opts: TrackOptions) {
  // Keep memory bounded; drop oldest.
  const MAX = 120;
  _queue.push({ type, opts, queuedAt: Date.now() });
  while (_queue.length > MAX) _queue.shift();
}

async function flushQueue(): Promise<void> {
  if (!services) return;
  if (_flushInFlight) return _flushInFlight;
  _flushInFlight = (async () => {
    // Wait a bit longer here; this is the "make sure user_uuid exists" path.
    try { await services.supabase.waitForJwt({ timeoutMs: 5000 }); } catch {}
    const tok = await getAccessTokenOrNull(services, { timeoutMs: 2000 });
    if (!tok) return; // keep queued; next call will retry
    await ensureExperimentsContextReady(2000);

    // Drop very old events (avoid sending stale junk after long background).
    const cutoff = Date.now() - 60_000;
    while (_queue.length && _queue[0]!.queuedAt < cutoff) _queue.shift();

    while (_queue.length) {
      const ev = _queue.shift()!;
      try {
        await sendEventWithToken(ev.type, ev.opts, tok);
      } catch {
        // If something goes wrong, stop flushing to avoid tight loops; re-enqueue remainder.
        enqueue(ev.type, ev.opts);
        break;
      }
    }
  })().finally(() => {
    _flushInFlight = null;
  });
  return _flushInFlight;
}

function uuidv4(): string {
  // Prefer native; fallback is good enough for analytics ids.
  try {
    return crypto.randomUUID();
  } catch {
    // eslint-disable-next-line no-bitwise
    const s4 = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).slice(1);
    return `${s4()}${s4()}-${s4()}-4${s4().slice(1)}-a${s4().slice(1)}-${s4()}${s4()}${s4()}`;
  }
}

// getClientSessionId moved to utils/clientSession.ts (canonical key + legacy migration)

function inferOrigin(): string {
  try {
    const p = String(window.location.pathname || '');
    if (p.includes('/chat')) return 'chat';
    if (p.includes('/profile')) return 'profile';
    if (p.includes('/topup')) return 'topup';
    // storefront is usually root
    return 'storefront';
  } catch {
    return 'other';
  }
}

function getPlatform(): string {
  try {
    return (window as any).Telegram?.WebApp?.platform || 'unknown';
  } catch {
    return 'unknown';
  }
}

function getAppVersion(): string {
  try {
    // Provided by Vite define in config (see vite.config.ts). Fallback for dev.
    // eslint-disable-next-line no-undef
    return (typeof __APP_VERSION__ !== 'undefined' && __APP_VERSION__) ? String(__APP_VERSION__) : 'dev';
  } catch {
    return 'dev';
  }
}

/**
 * Инициализация трекера (вызывается из bootstrap)
 */
export function initAnalytics(bootServices: BootServices): void {
  services = bootServices;
  pageStartTime = Date.now();
  try {
    window.addEventListener('aicams-user-scope-changed', () => {
      _experimentsContextCache = null;
      _experimentsRefreshOnce = null;
    });
  } catch {}
  // Best-effort flush: in case some events were queued before init completed.
  void flushQueue().catch(() => {});
}

async function sendEventWithToken(type: EventType, opts: TrackOptions, accessToken: string): Promise<void> {
  const origin = (opts.origin && String(opts.origin).trim()) ? String(opts.origin).trim() : inferOrigin();
  const expCtx = getExperimentsContext();
  const baseMetadata = (opts.metadata && typeof opts.metadata === 'object') ? opts.metadata : {};
  const existingExperiments = ((baseMetadata as any).experiments && typeof (baseMetadata as any).experiments === 'object')
    ? (baseMetadata as any).experiments
    : undefined;
  const mergedExperiments =
    expCtx.experiments
      ? { ...expCtx.experiments, ...(existingExperiments && typeof existingExperiments === 'object' ? existingExperiments : {}) }
      : existingExperiments;
  const metadata: Record<string, any> = {
    ...baseMetadata,
    model_name: opts.modelName,
  };
  if (mergedExperiments && typeof mergedExperiments === 'object' && Object.keys(mergedExperiments).length) {
    metadata.experiments = mergedExperiments;
  }
  if (expCtx.exp_config_version && metadata.exp_config_version == null) {
    metadata.exp_config_version = expCtx.exp_config_version;
  }

  await apiFetch('/api/analytics/event', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      event_type: type,
      event_id: uuidv4(),
      schema_version: 1,
      model_id: opts.modelId ?? null,
      chat_session_id: opts.chatSessionId ?? null,
      // Stage A naming: send client_session_id explicitly.
      // Keep session_id as a deprecated alias for backends that haven't been updated yet.
      client_session_id: opts.clientSessionId ?? getClientSessionId(),
      session_id: opts.clientSessionId ?? getClientSessionId(),
      origin,
      app_version: getAppVersion(),
      platform: getPlatform(),
      page: opts.page ?? window.location.pathname,
      url: window.location.href,
      metadata,
    }),
    keepalive: true,
  });
}

/**
 * Основная функция трекинга
 */
export async function trackEvent(type: EventType, opts: TrackOptions = {}): Promise<void> {
  if (!services) {
    console.warn('[Analytics] Not initialized');
    return;
  }

  try {
    // CRITICAL: do not send user events without JWT.
    // If JWT is not ready yet (fresh open, slow network), queue and flush later;
    // otherwise events get inserted with user_uuid=null and won't show in per-user Event Stream.
    const accessToken = await getAccessTokenOrNull(services, { timeoutMs: 800 });
    if (!accessToken) {
      enqueue(type, opts);
      void flushQueue().catch(() => {});
      return;
    }

    await ensureExperimentsContextReady(800);
    await sendEventWithToken(type, opts, accessToken);
  } catch (error) {
    console.warn('[Analytics] Failed to track:', type, error);
  }
}

// ============================================
// Convenience functions
// ============================================

/**
 * Трекинг просмотра страницы
 */
export function trackPageView(page: string, metadata?: Record<string, any>): void {
  pageStartTime = Date.now();
  trackEvent('page_view', { page, origin: page, metadata });
}

/**
 * Трекинг показа модели на витрине (impression/reach)
 */
export function trackImpression(modelId: number, modelName?: string): void {
  trackEvent('model_impression', { modelId, modelName, origin: 'storefront' });
}

/**
 * Трекинг клика на модель (переход в чат)
 */
export function trackModelView(modelId: number, modelName?: string): void {
  trackEvent('model_click', { modelId, modelName, origin: 'storefront' });
}

/**
 * Трекинг начала чата
 */
export function trackChatStart(modelId: number, modelName?: string, chatSessionId?: string): void {
  trackEvent('model_chat_start', { modelId, modelName, origin: 'chat', chatSessionId });
}

/**
 * Reset chat back to initial state (user pressed Reset).
 */
export function trackChatReset(modelId: number, modelName?: string, chatSessionId?: string): void {
  trackEvent('model_chat_reset', { modelId, modelName, origin: 'chat', chatSessionId });
}

/**
 * Трекинг использования команды
 */
export function trackCommand(commandType: string, modelId: number, creditsCost: number = 0): void {
  trackEvent('model_command_used', {
    modelId,
    metadata: { command_type: commandType, credits_cost: creditsCost },
  });
}

/**
 * Трекинг переключения между моделями
 */
// cross_chat removed (no UX yet)

/**
 * Трекинг добавления в избранное
 */
export function trackFavoriteAdd(modelId: number): void {
  trackEvent('favorite_add', { modelId });
}

/**
 * Трекинг удаления из избранного
 */
export function trackFavoriteRemove(modelId: number): void {
  trackEvent('favorite_remove', { modelId });
}

/**
 * Трекинг показа paywall (недостаточно кредитов)
 */
export function trackPaywallView(params: {
  modelId?: number | null;
  chatSessionId?: string | null;
  neededCredits?: number | null;
  balance?: number | null;
  origin?: string;
  reason?: string;
}): void {
  trackEvent('paywall_view', {
    modelId: params.modelId ?? undefined,
    chatSessionId: params.chatSessionId ?? undefined,
    origin: params.origin,
    metadata: {
      needed_credits: params.neededCredits ?? null,
      balance: params.balance ?? null,
      origin: params.origin || undefined,
      reason: params.reason || undefined,
    },
  });
}

export function trackPaywallCtaView(params: {
  modelId?: number | null;
  chatSessionId?: string | null;
  origin?: string;
  surface?: string; // e.g. chat_actions_overlay
  ctaKind: 'premium' | 'topup';
  reason?: string;
  neededCredits?: number | null;
  balance?: number | null;
  commandName?: string | null;
}): void {
  trackEvent('paywall_cta_view', {
    origin: params.origin,
    modelId: params.modelId ?? undefined,
    chatSessionId: params.chatSessionId ?? undefined,
    metadata: {
      origin: params.origin || undefined,
      surface: params.surface || undefined,
      cta_kind: params.ctaKind,
      reason: params.reason || undefined,
      needed_credits: params.neededCredits ?? null,
      balance: params.balance ?? null,
      command_name: params.commandName ?? null,
    },
  });
}

export function trackPaywallCtaClick(params: {
  modelId?: number | null;
  chatSessionId?: string | null;
  origin?: string;
  surface?: string; // e.g. chat_actions_overlay
  ctaKind: 'premium' | 'topup';
  reason?: string;
  neededCredits?: number | null;
  balance?: number | null;
  commandName?: string | null;
}): void {
  trackEvent('paywall_cta_click', {
    origin: params.origin,
    modelId: params.modelId ?? undefined,
    chatSessionId: params.chatSessionId ?? undefined,
    metadata: {
      origin: params.origin || undefined,
      surface: params.surface || undefined,
      cta_kind: params.ctaKind,
      reason: params.reason || undefined,
      needed_credits: params.neededCredits ?? null,
      balance: params.balance ?? null,
      command_name: params.commandName ?? null,
    },
  });
}

/**
 * Трекинг закрытия CTA кнопки без перехода в paywall (красный крестик)
 */
export function trackPaywallCtaDismiss(params: {
  modelId?: number | null;
  chatSessionId?: string | null;
  origin?: string;
  surface?: string;
  ctaKind: 'premium' | 'topup';
  reason?: string;
  neededCredits?: number | null;
  balance?: number | null;
  commandName?: string | null;
}): void {
  trackEvent('paywall_cta_dismiss', {
    origin: params.origin,
    modelId: params.modelId ?? undefined,
    chatSessionId: params.chatSessionId ?? undefined,
    metadata: {
      origin: params.origin || undefined,
      surface: params.surface || undefined,
      cta_kind: params.ctaKind,
      reason: params.reason || undefined,
      needed_credits: params.neededCredits ?? null,
      balance: params.balance ?? null,
      command_name: params.commandName ?? null,
    },
  });
}

/**
 * Трекинг нажатия на пакет для оплаты
 */
export function trackPaymentInitiated(
  packageId: number,
  credits: number,
  priceStars: number,
  origin?: string,
  ctx?: { modelId?: number | null; chatSessionId?: string | null },
  extraMetadata?: Record<string, any>
): void {
  trackEvent('payment_initiated', {
    origin,
    modelId: ctx?.modelId ?? undefined,
    chatSessionId: ctx?.chatSessionId ?? undefined,
    metadata: {
      ...(extraMetadata && typeof extraMetadata === 'object' ? extraMetadata : {}),
      package_id: packageId,
      credits,
      price_stars: priceStars,
      origin: origin || undefined,
    },
  });
}

/**
 * Payment initiation for subscription flows (Premium).
 * Stored in the same event_type ('payment_initiated') but with product-specific metadata.
 */
export function trackSubscriptionPaymentInitiated(params: {
  origin?: string;
  paymentMethod: 'apple_google_pay' | 'crypto' | 'card';
  provider: 'telegram' | 'nowpayments' | 'tribute' | 'centrobill';
  providerPaymentMethod: 'telegram_stars' | 'nowpayments' | 'tribute' | 'centrobill';
  selectionKind?: 'explicit' | 'implicit';
  plan: {
    id: number;
    name?: string;
    price_amount?: number;
    price_currency?: string;
    gross_amount_usd?: number;
    price_stars?: number;
    bonus_credits?: number;
    duration_days?: number;
  };
  refs?: {
    order_id?: string | null;
    invoice_id?: string | number | null;
    order_uuid?: string | null;
    reused?: boolean | null;
  };
  extraMetadata?: Record<string, any>;
}): void {
  const p = params?.plan;
  const refs = params?.refs;
  trackEvent('payment_initiated', {
    origin: params.origin,
    metadata: {
      ...(params.extraMetadata && typeof params.extraMetadata === 'object' ? params.extraMetadata : {}),
      product: 'subscription',
      flow: 'premium',
      selection_kind: params.selectionKind || undefined,
      provider: params.provider,
      payment_method: params.paymentMethod,
      provider_payment_method: params.providerPaymentMethod,
      plan_id: Number(p?.id || 0) || null,
      plan_name: p?.name ?? null,
      pricing_amount: p?.price_amount ?? null,
      pricing_currency: p?.price_currency ?? null,
      gross_amount_usd: p?.gross_amount_usd ?? null,
      price_stars: p?.price_stars ?? null,
      bonus_credits: p?.bonus_credits ?? null,
      duration_days: p?.duration_days ?? null,
      order_id: refs?.order_id ?? null,
      invoice_id: refs?.invoice_id ?? null,
      order_uuid: refs?.order_uuid ?? null,
      reused: refs?.reused ?? null,
      origin: params.origin || undefined,
    },
  });
}

export function trackSubscriptionPaymentFailed(params: {
  origin?: string;
  paymentMethod: 'apple_google_pay' | 'crypto' | 'card';
  provider: 'telegram' | 'nowpayments' | 'tribute' | 'centrobill';
  providerPaymentMethod: 'telegram_stars' | 'nowpayments' | 'tribute' | 'centrobill';
  status: string;
  selectionKind?: 'explicit' | 'implicit';
  plan: {
    id: number;
    name?: string;
    price_amount?: number;
    price_currency?: string;
    gross_amount_usd?: number;
    price_stars?: number;
    bonus_credits?: number;
    duration_days?: number;
  };
  refs?: {
    order_id?: string | null;
    invoice_id?: string | number | null;
    order_uuid?: string | null;
  };
  extraMetadata?: Record<string, any>;
}): void {
  const p = params?.plan;
  const refs = params?.refs;
  trackEvent('payment_failed', {
    origin: params.origin,
    metadata: {
      ...(params.extraMetadata && typeof params.extraMetadata === 'object' ? params.extraMetadata : {}),
      product: 'subscription',
      flow: 'premium',
      selection_kind: params.selectionKind || undefined,
      provider: params.provider,
      payment_method: params.paymentMethod,
      provider_payment_method: params.providerPaymentMethod,
      plan_id: Number(p?.id || 0) || null,
      plan_name: p?.name ?? null,
      pricing_amount: p?.price_amount ?? null,
      pricing_currency: p?.price_currency ?? null,
      gross_amount_usd: p?.gross_amount_usd ?? null,
      price_stars: p?.price_stars ?? null,
      bonus_credits: p?.bonus_credits ?? null,
      duration_days: p?.duration_days ?? null,
      status: String(params.status || '').trim() || 'unknown',
      order_id: refs?.order_id ?? null,
      invoice_id: refs?.invoice_id ?? null,
      order_uuid: refs?.order_uuid ?? null,
      origin: params.origin || undefined,
    },
  });
}

/**
 * Трекинг выбора пакета на paywall/topup
 */
export function trackPackageSelected(
  packageId: number,
  credits: number,
  priceStars: number,
  origin?: string,
  ctx?: { modelId?: number | null; chatSessionId?: string | null },
  extraMetadata?: Record<string, any>
): void {
  trackEvent('package_selected', {
    origin,
    modelId: ctx?.modelId ?? undefined,
    chatSessionId: ctx?.chatSessionId ?? undefined,
    metadata: {
      ...(extraMetadata && typeof extraMetadata === 'object' ? extraMetadata : {}),
      package_id: packageId,
      credits,
      price_stars: priceStars,
      origin: origin || undefined,
    },
  });
}

/**
 * Трекинг ошибки/отмены оплаты
 */
export function trackPaymentFailed(params: {
  packageId: number;
  credits: number;
  priceStars: number;
  status: string;
  origin?: string;
  modelId?: number | null;
  chatSessionId?: string | null;
  extraMetadata?: Record<string, any>;
}): void {
  trackEvent('payment_failed', {
    origin: params.origin,
    modelId: params.modelId ?? undefined,
    chatSessionId: params.chatSessionId ?? undefined,
    metadata: {
      ...(params.extraMetadata && typeof params.extraMetadata === 'object' ? params.extraMetadata : {}),
      package_id: params.packageId,
      credits: params.credits,
      price_stars: params.priceStars,
      status: params.status,
      origin: params.origin || undefined,
    },
  });
}

/**
 * Трекинг закрытия paywall/topup без оплаты
 */
export function trackPaywallDismissed(params: { timeSpentMs: number; origin?: string; packageId?: number | null }): void {
  trackEvent('paywall_dismissed', {
    origin: params.origin,
    metadata: {
      time_spent_ms: params.timeSpentMs,
      origin: params.origin || undefined,
      package_id: params.packageId ?? null,
    },
  });
}

/**
 * Topup sheet opened (can happen from chat/storefront/profile).
 */
export function trackTopupOpen(origin: string, ctx?: { modelId?: number | null; chatSessionId?: string | null }): void {
  trackEvent('topup_open', { origin, modelId: ctx?.modelId ?? undefined, chatSessionId: ctx?.chatSessionId ?? undefined });
}

/**
 * Premium paywall opened (can happen from chat/storefront/profile).
 */
export function trackPremiumOpen(origin: string, metadata?: Record<string, any>): void {
  trackEvent('premium_open', { origin, metadata });
}

/**
 * Трекинг открытия WebApp (вызывается один раз при старте приложения)
 * Используется для расчёта DAU/MAU и Retention
 */
export function trackWebappOpen(): void {
  const sid = getClientSessionId();
  if (_webappOpenSentForSession === sid) return;
  _webappOpenSentForSession = sid;
  trackEvent('webapp_open', {
    clientSessionId: sid,
    page: window.location.pathname,
    origin: inferOrigin(),
    metadata: {
      referrer: document.referrer || null,
      platform: getPlatform(),
    },
  });
}
